import { Injectable } from '@angular/core';
import { gsap } from 'gsap';
import { ScrambleTextPlugin } from 'gsap/ScrambleTextPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrambleTextPlugin, ScrollTrigger);

@Injectable({
  providedIn: 'root',
})
export class AnimationService {
  fadeIn(element: HTMLElement, duration = 0.6) {
    return gsap.fromTo(
      element,
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration, ease: 'power2.out' },
    );
  }

  slideInLeft(element: HTMLElement, duration = 0.5) {
    return gsap.fromTo(
      element,
      { x: -100, opacity: 0 },
      { x: 0, opacity: 1, duration, ease: 'power3.out' },
    );
  }

  scaleIn(element: HTMLElement, duration = 0.4) {
    return gsap.fromTo(
      element,
      { scale: 0.8, opacity: 0 },
      { scale: 1, opacity: 1, duration, ease: 'back.out(1.7)' },
    );
  }

  scrollAnimate(elements: string | HTMLElement[], config: Partial<gsap.TweenVars> = {}) {
    return gsap.from(elements, {
      opacity: 0,
      y: 80,
      duration: 1,
      stagger: 0.2,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: elements,
        start: 'top 80%',
        toggleActions: 'play reverse play reverse',
      },
      ...config,
    });
  }

  maskReveal(element: HTMLElement, duration = 1.2, yPercent = 120, delay = 0) {
    return gsap.from(element, {
      yPercent,
      opacity: 0,
      duration,
      ease: 'power4.out',
      delay,
    });
  }

  maskRevealStagger(
    container: HTMLElement,
    options: { duration?: number; yPercent?: number; stagger?: number; delay?: number } = {},
  ) {
    const { duration = 1, yPercent = 120, stagger = 0.1, delay = 0 } = options;

    // Wrap each line in a span for animation
    const lines: HTMLElement[] = [];
    container.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
        const span = document.createElement('span');
        span.style.display = 'block'; // ensures each line animates individually
        span.textContent = node.textContent;
        node.parentNode?.replaceChild(span, node);
        lines.push(span);
      } else if (node instanceof HTMLElement) {
        lines.push(node);
      }
    });

    return gsap.from(lines, {
      yPercent,
      opacity: 0,
      duration,
      ease: 'power4.out',
      stagger,
      delay,
    });
  }

  scrambleText(element: HTMLElement, text: string) {
    return gsap.fromTo(
      element,
      { opacity: 0 },
      {
        opacity: 1,
        duration: 2,
        scrambleText: {
          text,
          chars: 'upperCase',
          speed: 0.4,
        },
      },
    );
  }
}
